import type {
  ReportDraft,
  ReportGuidanceItem,
  ReportImportField,
  ReportIssue,
  ReportRow,
} from "./reportDraftApi";

const moneyFields = [
  { field: "bondNet", label: "Бонд", derived: false },
  { field: "stockNet", label: "Хувьцаа", derived: false },
  { field: "estateNet", label: "Үл хөдлөх", derived: false },
  { field: "cashAmount", label: "Бэлэн мөнгө", derived: false },
  { field: "bankFee", label: "Банкны шимтгэл", derived: false },
  { field: "grossAmount", label: "Нийт · шалгалт", derived: true },
  { field: "transferAmount", label: "Шилжүүлэх · шалгалт", derived: true },
] as const;

export type GuidanceComparison = {
  field: string;
  label: string;
  derived: boolean;
  excelValue: string | null;
  savedValue: string | null;
  delta: string | null;
};
export type GuidanceFilter =
  | "all"
  | "difference"
  | "unlinked"
  | "missing"
  | "audited"
  | "unknown";
export type GuidanceTableRow = {
  requestId: string;
  displayName: string;
  email: string | null;
  coverage: "Matched" | "Unlinked" | "Ambiguous" | "NotImported";
  comparisons: GuidanceComparison[];
  assetDelta: string | null;
  items: ReportGuidanceItem[];
  groupCount: number;
  hasDifference: boolean;
  hasMissing: boolean;
  hasAudited: boolean;
  unknown: boolean;
};

export const coverageLabels: Record<GuidanceTableRow["coverage"], string> = {
  Matched: "Excel мөртэй холбогдсон",
  Unlinked: "Excel-тэй тодорхой холбогдоогүй",
  Ambiguous: "Мөрийн холбоос тодорхойгүй",
  NotImported: "Excel тулгалт хийгдээгүй",
};

// Monetary arithmetic stays in integer cents; null is never coerced to zero.
export function guidanceCents(value: string | null | undefined): bigint | null {
  if (typeof value !== "string") return null;
  const match = /^(-?)(0|[1-9]\d*)(?:\.(\d{1,2}))?$/.exec(value);
  if (!match) return null;
  const cents =
    BigInt(match[2]) * 100n + BigInt((match[3] ?? "").padEnd(2, "0"));
  return match[1] ? -cents : cents;
}

function decimal(cents: bigint): string {
  const positive = cents < 0n ? -cents : cents;
  return `${cents < 0n ? "-" : ""}${positive / 100n}.${String(positive % 100n).padStart(2, "0")}`;
}

export function guidanceDelta(
  savedValue: string | null | undefined,
  excelValue: string | null | undefined,
): string | null {
  const saved = guidanceCents(savedValue),
    excel = guidanceCents(excelValue);
  return saved === null || excel === null ? null : decimal(saved - excel);
}

export function guidanceMoneyText(
  value: string | null,
  signed = false,
): string {
  const cents = guidanceCents(value);
  if (cents === null) return "Дутуу";
  const [whole, fraction] = decimal(cents < 0n ? -cents : cents).split(".");
  const sign = cents < 0n ? "−" : signed && cents > 0n ? "+" : "";
  return `${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction}`;
}

export function groupGuidanceItems(
  items: readonly ReportGuidanceItem[],
): ReportGuidanceItem[] {
  const groups = new Map<string, ReportGuidanceItem>();
  const componentConflict = items.some(
    (item) =>
      item.basis === "Generic" &&
      ["IMPORT_VALUE_CONFLICT", "CHECK_IMPORT_VALUE_CONFLICT"].includes(
        item.code,
      ) &&
      moneyFields.some((field) => !field.derived && field.field === item.field),
  );
  for (const original of items) {
    let item = original;
    const conflict =
      item.basis === "Generic" &&
      ["IMPORT_VALUE_CONFLICT", "CHECK_IMPORT_VALUE_CONFLICT"].includes(
        item.code,
      );
    if (
      conflict &&
      ["grossAmount", "transferAmount"].includes(item.field ?? "")
    ) {
      if (componentConflict) continue;
      item = {
        ...item,
        code: "TOTAL_RECONCILIATION",
        field: null,
        title: "Нийт дүнгийн тулгалт",
        explanation:
          "Нийт эсвэл шилжүүлэх дүн зөрсөн. Бүрэлдэхүүн, банкны шимтгэл болон тоймлолтыг хамтад нь нягтална уу.",
      };
    }
    if (conflict && ["bankName", "accountNo"].includes(item.field ?? ""))
      item = { ...item, code: "BANK_DETAILS", field: null };
    const key = `${item.code}\u0000${item.field ?? ""}\u0000${item.basis}`;
    const previous = groups.get(key);
    if (!previous) {
      groups.set(key, {
        ...item,
        evidenceRequired: [...item.evidenceRequired],
        evidence: [...item.evidence],
      });
      continue;
    }
    const rank = { info: 0, warning: 1, error: 2 };
    groups.set(key, {
      ...previous,
      explanation: [...new Set([previous.explanation, item.explanation])].join(
        " ",
      ),
      action: [...new Set([previous.action, item.action])].join(" "),
      severity:
        rank[item.severity] > rank[previous.severity]
          ? item.severity
          : previous.severity,
      evidenceRequired: [
        ...new Set([...previous.evidenceRequired, ...item.evidenceRequired]),
      ],
      evidence: [
        ...new Map(
          [...previous.evidence, ...item.evidence].map((entry) => [
            `${entry.label}\u0000${entry.reference}`,
            entry,
          ]),
        ).values(),
      ],
    });
  }
  return [...groups.values()];
}

export function visibleGuidanceItems(
  items: readonly ReportGuidanceItem[],
  stale = false,
): ReportGuidanceItem[] {
  const grouped = groupGuidanceItems(items);
  if (!stale || !grouped.some((item) => item.basis === "Audited"))
    return grouped;
  return [
    {
      code: "STALE_AUDIT",
      field: null,
      title: "Өмнөх шалгалтын тайлбарыг дахин нягтлах",
      explanation:
        "Одоогийн эх, хувилбарыг баталгаажуулах шаардлагатай. Хадгалаагүй засвар, эхийн өөрчлөлт эсвэл мэдээлэл унших алдаа байж болно. Өмнөх шалгалтын дүгнэлтийг шууд хэрэглэхгүй.",
      action:
        "Хадгалаагүй засвараа нягталж, эх болон хувилбарыг амжилттай уншсаны дараа тайлбарыг дахин хянана уу.",
      owner: "Санхүүгийн хянагч",
      evidenceRequired: ["Шинэ хувилбарын тулгалт", "Холбогдох эх баримт"],
      evidence: [],
      basis: "Generic",
      severity: "warning",
    },
    ...grouped.filter((item) => item.basis !== "Audited"),
  ];
}

function genericItem(
  code: string,
  field: string | null,
  title: string,
  explanation: string,
  severity: ReportGuidanceItem["severity"] = "error",
): ReportGuidanceItem {
  return {
    code,
    field,
    title,
    explanation,
    severity,
    action:
      "Excel-ийн эх мөр болон хадгалсан тайланг холбогдох баримттай тулгаж, зөв утга болон зөрүүний шалтгааныг хариуцсан ажилтнаар батлуулна уу.",
    owner: "Хариуцсан менежер · Санхүү",
    evidenceRequired: [
      "Excel-ийн эх мөр",
      "Хадгалсан буцаалтын баримт",
      "Зөрүүний тайлбар",
    ],
    evidence: [],
    basis: "Generic",
  };
}

function fallbackGuidance(
  issues: ReportIssue[],
  comparisons: GuidanceComparison[],
  coverage: GuidanceTableRow["coverage"],
): ReportGuidanceItem[] {
  const result: ReportGuidanceItem[] = [];
  const hasComponentIssue = issues.some(
    (item) =>
      !["grossAmount", "transferAmount"].includes(item.field ?? "") &&
      ["IMPORT_VALUE_CONFLICT", "IMPORT_MISSING_VALUE"].includes(item.code),
  );
  for (const issue of issues) {
    if (
      ["IMPORT_MISSING_REQUEST", "IMPORT_AMBIGUOUS_EMAIL"].includes(issue.code)
    )
      continue;
    if (
      ["IMPORT_VALUE_CONFLICT", "IMPORT_MISSING_VALUE"].includes(issue.code) &&
      ["grossAmount", "transferAmount"].includes(issue.field ?? "")
    ) {
      if (!hasComponentIssue)
        result.push(
          genericItem(
            "TOTAL_RECONCILIATION",
            null,
            "Нийт дүнгийн тулгалт",
            "Нийт эсвэл шилжүүлэх дүн зөрсөн. Бүрэлдэхүүн, банкны шимтгэл болон тоймлолтыг хамтад нь нягтална уу.",
            issue.severity,
          ),
        );
      continue;
    }
    if (
      ["IMPORT_VALUE_CONFLICT", "IMPORT_MISSING_VALUE"].includes(issue.code) &&
      ["bankName", "accountNo"].includes(issue.field ?? "")
    ) {
      result.push(
        genericItem(
          "BANK_DETAILS",
          null,
          "Банкны мэдээллийг нягтлах",
          "Excel ба хадгалсан тайлангийн банкны мэдээлэл дутуу эсвэл зөрүүтэй байна. Зөв дансыг эх баримтаар батална.",
          issue.severity,
        ),
      );
      continue;
    }
    result.push(
      genericItem(
        issue.code,
        issue.field ?? null,
        "Баримтаар тодруулах зүйл",
        issue.message,
        issue.severity,
      ),
    );
  }
  if (coverage !== "Matched")
    result.unshift(
      genericItem(
        "REQUEST_COVERAGE",
        null,
        coverage === "NotImported"
          ? "Excel тулгалт хийгдээгүй"
          : "Excel-тэй тодорхой холбогдоогүй хүсэлт",
        coverage === "NotImported"
          ? "Энэ хүсэлтийг эх Excel-тэй тулгасан мэдээлэл байхгүй байна."
          : "Энэ хүсэлт нэг Excel мөртэй тодорхой холбогдоогүй. Хамрах хүрээ, email болон давхардсан мөрийг нягтална. Энэ нь төлбөр хийгдээгүйг дангаараа нотлохгүй.",
      ),
    );
  if (!result.length) {
    for (const comparison of comparisons.filter(
      (item) =>
        !item.derived &&
        item.delta !== null &&
        guidanceCents(item.delta) !== 0n,
    ))
      result.push(
        genericItem(
          "SAVED_REPORT_DIFFERENCE",
          comparison.field,
          `${comparison.label}: дүн зөрсөн`,
          "Excel-ийн утга хадгалсан тайлангийн утгатай таарахгүй байна. Тодорхой шалтгааныг энэ тулгалтаас дангаар нь тогтоохгүй.",
        ),
      );
  }
  return groupGuidanceItems(result);
}

function savedMoney(
  row: ReportRow,
  field: (typeof moneyFields)[number]["field"],
): string | null {
  const value = row[field];
  return typeof value === "object" && value !== null ? value.value : value;
}

const normalize = (value: string) =>
  value.normalize("NFKC").trim().toLocaleLowerCase("mn");

export function buildReportGuidanceRows(
  report: ReportDraft,
  stale = false,
): GuidanceTableRow[] {
  return report.rows.map((row) => {
    const linked =
      report.importInfo?.rows.filter(
        (item) => item.requestId === row.requestId,
      ) ?? [];
    const ambiguousEmail =
      !!row.email &&
      report.importInfo?.rows.some(
        (item) =>
          item.matchStatus === "Ambiguous" &&
          item.email &&
          normalize(item.email) === normalize(row.email!),
      );
    const matched =
      linked.length === 1 && linked[0].matchStatus === "Matched"
        ? linked[0]
        : null;
    const coverage: GuidanceTableRow["coverage"] = !report.importInfo
      ? "NotImported"
      : linked.length > 1 ||
          ambiguousEmail ||
          linked.some((item) => item.matchStatus === "Ambiguous")
        ? "Ambiguous"
        : matched
          ? "Matched"
          : "Unlinked";
    const comparisons = moneyFields.flatMap(({ field, label, derived }) => {
      const candidates: ReportImportField[] =
        matched?.fields.filter((item) => item.field === field) ?? [];
      const savedValue = savedMoney(row, field);
      if (
        field === "estateNet" &&
        !candidates.length &&
        row.estateCount === 0 &&
        guidanceCents(savedValue) === 0n
      )
        return [];
      const excelValue =
        candidates.length === 1 ? candidates[0].excelValue : null;
      return [
        {
          field,
          label,
          derived,
          savedValue,
          excelValue,
          delta: guidanceDelta(savedValue, excelValue),
        },
      ];
    });
    const bonds = guidanceCents(
      comparisons.find((item) => item.field === "bondNet")?.delta,
    );
    const stocks = guidanceCents(
      comparisons.find((item) => item.field === "stockNet")?.delta,
    );
    const serverItems =
      report.rowGuidance
        ?.filter((item) => item.requestId === row.requestId)
        .flatMap((item) => item.items) ?? [];
    const issues = report.issues.filter(
      (issue) => issue.requestId === row.requestId,
    );
    const unrepresented = issues.filter(
      (issue) =>
        !serverItems.some(
          (item) =>
            ([issue.code, `CHECK_${issue.code}`].includes(item.code) &&
              item.field === (issue.field ?? null)) ||
            (item.code === "CHECK_DERIVED_TOTALS" &&
              issue.code === "IMPORT_VALUE_CONFLICT" &&
              ["grossAmount", "transferAmount"].includes(issue.field ?? "")) ||
            (item.code === "CHECK_EXCEL_ARITHMETIC" &&
              [
                "SUMMARY_COMPONENT_DIFFERENCE",
                "SUMMARY_TOTAL_DIFFERENCE",
                "DETAIL_SUMMARY_DIFFERENCE",
              ].includes(issue.code)),
        ),
    );
    const guidance = serverItems.length
      ? [
          ...serverItems,
          ...unrepresented.map((issue) =>
            genericItem(
              issue.code,
              issue.field ?? null,
              "Баримтаар тодруулах зүйл",
              issue.message,
              issue.severity,
            ),
          ),
        ]
      : fallbackGuidance(issues, comparisons, coverage);
    const items = visibleGuidanceItems(guidance, stale || report.sourceChanged);
    return {
      requestId: row.requestId,
      displayName: row.displayName,
      email: row.email,
      coverage,
      comparisons,
      assetDelta:
        bonds === null || stocks === null ? null : decimal(bonds + stocks),
      items,
      groupCount: items.length,
      hasDifference: comparisons.some(
        (item) => item.delta !== null && guidanceCents(item.delta) !== 0n,
      ),
      hasMissing: comparisons.some(
        (item) => !item.derived && item.delta === null,
      ),
      hasAudited: items.some((item) => item.basis === "Audited"),
      unknown: !items.length,
    };
  });
}

export function filterReportGuidanceRows(
  rows: GuidanceTableRow[],
  search: string,
  filter: GuidanceFilter,
): GuidanceTableRow[] {
  const term = normalize(search);
  return rows.filter((row) => {
    const selected =
      filter === "all" ||
      (filter === "difference" && row.hasDifference) ||
      (filter === "unlinked" && row.coverage !== "Matched") ||
      (filter === "missing" && row.hasMissing) ||
      (filter === "audited" && row.hasAudited) ||
      (filter === "unknown" && row.unknown);
    const text = [
      row.displayName,
      row.email ?? "",
      ...row.items.flatMap((item) => [
        item.title,
        item.explanation,
        item.action,
        item.owner,
      ]),
    ].join(" ");
    return selected && (!term || normalize(text).includes(term));
  });
}
